import React, { useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Code, Globe, Smartphone, Monitor } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TestingUtils } from "@/utils/testing";

interface DNITestSuiteProps {
  onTestResult: (result: any) => void;
  onTestLog: (log: any) => void;
  isRunning: boolean;
  setIsRunning: (running: boolean) => void;
}

export interface DNITestSuiteRef {
  runAllTests: () => Promise<void>;
}

/**
 * Enhanced DNI (Dynamic Number Insertion) Testing Suite
 * Tests script generation, number replacement, and different format support
 */
export const DNITestSuite = forwardRef<DNITestSuiteRef, DNITestSuiteProps>(({
  onTestResult,
  onTestLog,
  isRunning,
  setIsRunning
}, ref) => {
  const [selectedFormat, setSelectedFormat] = useState<string>("e164");
  const [testWebsite, setTestWebsite] = useState<string>("https://example.com");
  const [currentTest, setCurrentTest] = useState<string>("");
  const [customHTML, setCustomHTML] = useState<string>("");

  // Expose runAllTests to parent via ref
  const runAllDNITestsRef = React.useRef<(() => Promise<void>) | null>(null);
  
  useImperativeHandle(ref, () => ({
    runAllTests: async () => {
      if (runAllDNITestsRef.current) {
        await runAllDNITestsRef.current();
      }
    }
  }), []);
  /**
   * Helper function to log test events
   */
  const log = useCallback((level: "info" | "warn" | "error" | "success", message: string, details?: any) => {
    console.log(`[DNI] ${level.toUpperCase()}: ${message}`, details);
    onTestLog({
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      level,
      message,
      details,
      category: "dni"
    });
  }, [onTestLog]);

  /**
   * Create a standardized test result
   */
  const createTestResult = useCallback((name: string, success: boolean, details?: string, error?: string) => {
    const now = new Date();
    return {
      id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      testName: name, // Primary field
      name, // For backward compatibility
      status: success ? "success" : "failure" as const,
      startTime: now,
      endTime: now,
      duration: 0,
      details,
      error,
      errorMessage: error, // For TestingComponent compatibility
      category: "dni"
    };
  }, []);

  /**
   * Test DNI script generation with multiple format support
   * @returns Test result with generation status and performance metrics
   */
  const testDNIScriptGeneration = async () => {
    log("info", "Testing DNI script generation...");
    
    try {
      const result = await TestingUtils.testEdgeFunction("Calie_dniScript", {
        website: testWebsite,
        format: selectedFormat
      });

      if (result.success) {
        log("success", "DNI script generation test completed successfully", result);
        return createTestResult(
          "DNI Script Generation", 
          true, 
          `Generated script for ${testWebsite} with ${selectedFormat} format. Response time: ${result.responseTime.toFixed(2)}ms`
        );
      } else {
        log("error", "DNI script generation test failed", result);
        return createTestResult("DNI Script Generation", false, undefined, result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "DNI script generation test exception", { error: errorMessage });
      return createTestResult("DNI Script Generation", false, undefined, errorMessage);
    }
  };

  /**
   * Test number format conversions
   */
  const testNumberFormats = async () => {
    log("info", "Testing number format conversions...");
    
    try {
      const testNumbers = [
        "+15551234567",  // E.164 format
        "(555) 123-4567", // National format
        "555.123.4567",   // Dot format
        "555-123-4567",   // Dash format
        "5551234567"      // Plain format
      ];

      const results = [];
      
      for (const number of testNumbers) {
        const formats = TestingUtils.testNumberFormats(number);
        results.push({
          input: number,
          formats
        });
        log("info", `Converted ${number}`, formats);
      }

      const allValid = results.every(r => r.formats.isValid);
      
      if (allValid) {
        log("success", "Number format conversion test completed successfully", results);
        return createTestResult(
          "Number Format Conversion", 
          true, 
          `Successfully converted ${results.length} test numbers to all formats`
        );
      } else {
        const invalidCount = results.filter(r => !r.formats.isValid).length;
        log("warn", `Number format conversion had ${invalidCount} invalid results`, results);
        return createTestResult(
          "Number Format Conversion", 
          false, 
          undefined, 
          `${invalidCount} out of ${results.length} number conversions failed`
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Number format conversion test exception", { error: errorMessage });
      return createTestResult("Number Format Conversion", false, undefined, errorMessage);
    }
  };

  /**
   * Test number replacement functionality
   */
  const testNumberReplacement = async () => {
    log("info", "Testing number replacement functionality...");
    
    try {
      // Test HTML with various phone number formats
      const testHTML = `
        <div>
          <p>Call us at <span class="phone">555-123-4567</span></p>
          <div>Phone: (555) 123-4567</div>
          <a href="tel:+15551234567">+1 555 123 4567</a>
          <span data-phone="555.123.4567">Contact</span>
        </div>
      `;

      const trackingNumber = TestingUtils.generateTestPhoneNumber();
      
      // Simulate number replacement (this would be done by the DNI script)
      const replacedHTML = testHTML.replace(
        /(\+?1?[-.\s]?\(?5{3}\)?[-.\s]?1{3}[-.\s]?4{4})/g,
        trackingNumber
      );
      
      const replacementCount = (testHTML.match(/(\+?1?[-.\s]?\(?5{3}\)?[-.\s]?1{3}[-.\s]?4{4})/g) || []).length;
      
      if (replacementCount > 0) {
        log("success", "Number replacement test completed successfully", {
          originalHTML: testHTML,
          replacedHTML,
          trackingNumber,
          replacementCount
        });
        return createTestResult(
          "Number Replacement", 
          true, 
          `Successfully replaced ${replacementCount} phone numbers with tracking number ${trackingNumber}`
        );
      } else {
        log("warn", "No phone numbers found for replacement", { testHTML });
        return createTestResult("Number Replacement", false, undefined, "No phone numbers detected in test HTML");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Number replacement test exception", { error: errorMessage });
      return createTestResult("Number Replacement", false, undefined, errorMessage);
    }
  };

  /**
   * Test DNI script loading and execution
   */
  const testDNIScriptExecution = async () => {
    log("info", "Testing DNI script loading and execution...");
    
    try {
      // Simulate script execution environment
      const mockWindow = {
        document: {
          querySelectorAll: () => [],
          getElementsByTagName: () => [],
          createElement: () => ({ textContent: "", setAttribute: () => {} })
        },
        location: { href: testWebsite },
        console: { log: () => {}, error: () => {} }
      };

      // Test script generation with different selectors
      const selectors = [
        'a[href^="tel:"]',
        '.phone',
        '[data-phone]',
        'span:contains("555")'
      ];

      let successCount = 0;
      
      for (const selector of selectors) {
        try {
          // Simulate selector testing
          log("info", `Testing selector: ${selector}`);
          successCount++;
        } catch (err) {
          log("warn", `Selector failed: ${selector}`, err);
        }
      }

      if (successCount === selectors.length) {
        log("success", "DNI script execution test completed successfully", {
          selectorsTest: successCount,
          mockEnvironment: Object.keys(mockWindow)
        });
        return createTestResult(
          "DNI Script Execution", 
          true, 
          `Successfully tested ${successCount} selectors in mock environment`
        );
      } else {
        log("warn", `DNI script execution partially successful: ${successCount}/${selectors.length}`);
        return createTestResult(
          "DNI Script Execution", 
          false, 
          undefined, 
          `Only ${successCount} out of ${selectors.length} selectors worked`
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "DNI script execution test exception", { error: errorMessage });
      return createTestResult("DNI Script Execution", false, undefined, errorMessage);
    }
  };

  /**
   * Test different device types and responsive behavior
   */
  const testResponsiveDNI = async () => {
    log("info", "Testing responsive DNI behavior across device types...");
    
    try {
      const deviceTypes = [
        { name: "Desktop", width: 1920, height: 1080, userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64)" },
        { name: "Tablet", width: 768, height: 1024, userAgent: "Mozilla/5.0 (iPad; CPU OS 14_0 like Mac OS X)" },
        { name: "Mobile", width: 375, height: 667, userAgent: "Mozilla/5.0 (iPhone; CPU iPhone OS 14_0 like Mac OS X)" }
      ];

      const results = [];
      
      for (const device of deviceTypes) {
        // Simulate device-specific DNI behavior
        const trackingNumber = TestingUtils.generateTestPhoneNumber();
        const format = TestingUtils.testNumberFormats(trackingNumber);
        
        let displayFormat = format.e164;
        if (device.name === "Mobile") {
          displayFormat = format.national; // More user-friendly on mobile
        } else if (device.name === "Desktop") {
          displayFormat = format.international;
        }

        results.push({
          device: device.name,
          trackingNumber,
          displayFormat,
          dimensions: `${device.width}x${device.height}`
        });
        
        log("info", `Tested ${device.name} DNI behavior`, {
          device: device.name,
          trackingNumber,
          displayFormat
        });
      }

      log("success", "Responsive DNI test completed successfully", results);
      return createTestResult(
        "Responsive DNI", 
        true, 
        `Successfully tested DNI behavior across ${results.length} device types`
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Responsive DNI test exception", { error: errorMessage });
      return createTestResult("Responsive DNI", false, undefined, errorMessage);
    }
  };

  /**
   * Test custom HTML number replacement
   */
  const testCustomHTML = async () => {
    if (!customHTML) {
      log("warn", "No custom HTML provided for testing");
      return createTestResult("Custom HTML Test", false, undefined, "No HTML content provided");
    }

    log("info", "Testing custom HTML number replacement...");
    
    try {
      const trackingNumber = TestingUtils.generateTestPhoneNumber();
      
      // Common phone number patterns
      const phonePatterns = [
        /\+?1?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?[2-9]\d{2}[-.\s]?\d{4}/g, // US phone numbers
        /\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/g, // Basic format
        /tel:[+]?[0-9]+/g // Tel links
      ];

      let replacedHTML = customHTML;
      let totalReplacements = 0;

      for (const pattern of phonePatterns) {
        const matches = customHTML.match(pattern) || [];
        totalReplacements += matches.length;
        replacedHTML = replacedHTML.replace(pattern, trackingNumber);
      }

      if (totalReplacements > 0) {
        log("success", "Custom HTML number replacement completed successfully", {
          originalHTML: customHTML,
          replacedHTML,
          trackingNumber,
          totalReplacements
        });
        return createTestResult(
          "Custom HTML Test", 
          true, 
          `Successfully replaced ${totalReplacements} phone numbers in custom HTML`
        );
      } else {
        log("warn", "No phone numbers found in custom HTML", { customHTML });
        return createTestResult("Custom HTML Test", false, undefined, "No phone numbers detected in provided HTML");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log("error", "Custom HTML test exception", { error: errorMessage });
      return createTestResult("Custom HTML Test", false, undefined, errorMessage);
    }
  };

  /**
   * Run all DNI tests
   */
  const runAllDNITests = async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    log("info", "Starting comprehensive DNI testing...");

    const tests = [
      { name: "DNI Script Generation", fn: testDNIScriptGeneration },
      { name: "Number Formats", fn: testNumberFormats },
      { name: "Number Replacement", fn: testNumberReplacement },
      { name: "Script Execution", fn: testDNIScriptExecution },
      { name: "Responsive DNI", fn: testResponsiveDNI }
    ];

    let passed = 0;
    let failed = 0;

    for (const test of tests) {
      try {
        setCurrentTest(test.name);
        log("info", `Running ${test.name} test...`);
        
        const result = await test.fn();
        onTestResult(result);
        
        if (result.status === "success") {
          passed++;
        } else {
          failed++;
        }
        
        // Wait between tests
        await TestingUtils.sleep(500);
      } catch (error) {
        failed++;
        const errorMessage = error instanceof Error ? error.message : String(error);
        log("error", `Test ${test.name} failed with exception: ${errorMessage}`);
        onTestResult(createTestResult(test.name, false, undefined, errorMessage));
      }
    }

    setCurrentTest("");
    setIsRunning(false);
    
    log("success", `DNI testing completed. Passed: ${passed}, Failed: ${failed}`);
    onTestResult(createTestResult(
      "DNI Test Suite", 
      failed === 0, 
      `Completed ${tests.length} tests. Passed: ${passed}, Failed: ${failed}`
    ));
  };

  // Assign runAllDNITests to ref for imperative handle
  React.useEffect(() => {
    runAllDNITestsRef.current = runAllDNITests;
  }, [runAllDNITests]);

  return (
    <div className="space-y-6">
      {/* DNI Test Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-accent" />
            Enhanced DNI Testing
          </CardTitle>
          <CardDescription>
            Test DNI script generation, number replacement, and format support across devices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="website">Test Website</Label>
              <Input
                id="website"
                value={testWebsite}
                onChange={(e) => setTestWebsite(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="format">Number Format</Label>
              <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="e164">E.164 (+15551234567)</SelectItem>
                  <SelectItem value="national">National ((555) 123-4567)</SelectItem>
                  <SelectItem value="international">International (+1 555 123 4567)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button 
              onClick={runAllDNITests} 
              disabled={isRunning}
              className="bg-gradient-to-r from-accent to-accent/80"
            >
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Code className="h-4 w-4 mr-2" />}
              Test All DNI Features
            </Button>
          </div>

          {isRunning && currentTest && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Running</Badge>
              <span className="text-sm text-muted-foreground">Currently testing: {currentTest}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom HTML Testing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Custom HTML Testing
          </CardTitle>
          <CardDescription>
            Test DNI replacement on your own HTML content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder='<div><p>Call us at <span class="phone">555-123-4567</span></p><a href="tel:+15551234567">Contact Us</a></div>'
            value={customHTML}
            onChange={(e) => setCustomHTML(e.target.value)}
            className="min-h-[120px] font-mono text-sm"
          />
          
          <Button 
            onClick={testCustomHTML} 
            disabled={!customHTML || isRunning}
            variant="outline"
          >
            {isRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Globe className="h-4 w-4 mr-2" />}
            Test Custom HTML
          </Button>
        </CardContent>
      </Card>

      {/* Device Type Testing */}
      <Card>
        <CardHeader>
          <CardTitle>DNI Device Support</CardTitle>
          <CardDescription>
            Test DNI behavior across different device types and screen sizes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
              <Monitor className="h-8 w-8 text-primary" />
              <div>
                <div className="font-medium">Desktop</div>
                <div className="text-sm text-muted-foreground">1920x1080 • International format</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
              <Globe className="h-8 w-8 text-primary" />
              <div>
                <div className="font-medium">Tablet</div>
                <div className="text-sm text-muted-foreground">768x1024 • E.164 format</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
              <Smartphone className="h-8 w-8 text-primary" />
              <div>
                <div className="font-medium">Mobile</div>
                <div className="text-sm text-muted-foreground">375x667 • National format</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

DNITestSuite.displayName = 'DNITestSuite';